import { stripHtml } from "@/lib/citationUtils";

export type GmailMimePart = {
  mimeType?: string;
  body?: { data?: string; size?: number };
  parts?: GmailMimePart[];
};

export function decodeBase64Url(data: string): string {
  const padded = data.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
}

function decodePartData(part: GmailMimePart): string {
  if (!part.body?.data) return "";
  try {
    return decodeBase64Url(part.body.data);
  } catch {
    return "";
  }
}

function collectParts(
  payload: GmailMimePart,
  acc: { plain: string[]; html: string[] }
): void {
  const mime = payload.mimeType?.toLowerCase() ?? "";

  if (mime.startsWith("multipart/")) {
    for (const part of payload.parts ?? []) {
      collectParts(part, acc);
    }
    return;
  }

  const decoded = decodePartData(payload);
  if (!decoded) return;

  if (mime === "text/plain") {
    acc.plain.push(decoded);
  } else if (mime === "text/html") {
    acc.html.push(decoded);
  } else if (!payload.parts?.length && decoded.trim()) {
    if (decoded.trim().startsWith("<")) {
      acc.html.push(decoded);
    } else {
      acc.plain.push(decoded);
    }
  }
}

export function parseGmailPayload(payload: GmailMimePart): { body: string; bodyHtml?: string } {
  const acc = { plain: [] as string[], html: [] as string[] };
  collectParts(payload, acc);

  const plain = acc.plain.join("\n\n").trim();
  const html = acc.html.join("\n\n").trim();

  if (plain) {
    return { body: plain, bodyHtml: html || undefined };
  }

  if (html) {
    return { body: stripHtml(html), bodyHtml: html };
  }

  return { body: "" };
}

export function looksLikeHtml(text: string): boolean {
  const t = text.trim().toLowerCase();
  return t.startsWith("<!doctype") || t.startsWith("<html") || /<[a-z][\s\S]*>/i.test(t.slice(0, 500));
}
